import { Injectable } from '@angular/core';
import { BaseApiService, Result, ResultType, aaDataResponse } from '@shared/data-access';
import { TableData, TableDataResponse } from '@shared/ui/tables';
import { Observable, first, map, of, switchMap } from 'rxjs';
import { OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { UserRole } from '@shared/utils/base-auth';
import { BaseReportsService, BaseUserResponse, DailyReportsResponse, SummaryReportsResponse } from '@shared/feature/reports';

@Injectable({
  providedIn: 'root',
})
export class ReportsService extends BaseReportsService {
  constructor(api: BaseApiService, private currentOfficerFacade: OfficerDetailFacade) {
    super(api);
  }

  override fetchDailyReports(query: string): Observable<Result<TableDataResponse<DailyReportsResponse>>> {
    return this.currentOfficerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((loggedInOfficer) => {
        const role = loggedInOfficer.role;

        switch (role) {
          case UserRole.Commander:
          case UserRole.SuperAdmin:
            return this.api.get<aaDataResponse<DailyReportsResponse>>(`api/users/statistics/daily?${query}`).pipe(
              map((response) => {
                const tableData = new TableData(
                  response.iTotalRecords,
                  response.aaData.map(
                    (values) =>
                      new DailyReportsResponse(
                        new BaseUserResponse(values.User.FirstName, values.User.LastName, values.User.EmployeeID, values.User.UID, values.User.PictureFullPath),
                        values.TotalVideos,
                        values.TotalPhotos,
                        values.TotalAudios,
                        values.TotalDocuments,
                        values.TotalOthers,
                        values.CreatedOn
                      )
                  )
                );

                return {
                  value: tableData,
                  isSuccess: true,
                  isFailure: false,
                  isEmpty: false,
                  message: '',
                  httpStatusCode: 200,
                  resultType: ResultType.Ok,
                };
              })
            );

          default:
            return of({
              value: new TableData(0, []),
              isSuccess: true,
              isFailure: false,
              isEmpty: false,
              message: '',
              httpStatusCode: 200,
              resultType: ResultType.Ok,
            });
        }
      })
    );
  }

  override fetchSummaryReports(query: string): Observable<Result<TableDataResponse<SummaryReportsResponse>>> {
    return this.currentOfficerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((loggedInOfficer) => {
        const role = loggedInOfficer.role;

        switch (role) {
          case UserRole.Commander:
          case UserRole.SuperAdmin:
            return this.api.get<aaDataResponse<SummaryReportsResponse>>(`api/users/statistics/summary?${query}`).pipe(
              map((response) => {
                const tableData = new TableData(
                  response.iTotalRecords,
                  response.aaData.map(
                    (values) =>
                      new SummaryReportsResponse(
                        new BaseUserResponse(values.User.FirstName, values.User.LastName, values.User.EmployeeID, values.User.UID, values.User.PictureFullPath),
                        values.TotalVideos,
                        values.TotalPhotos,
                        values.TotalAudios,
                        values.TotalDocuments,
                        values.TotalOthers
                      )
                  )
                );

                return {
                  value: tableData,
                  isSuccess: true,
                  isFailure: false,
                  isEmpty: false,
                  message: '',
                  httpStatusCode: 200,
                  resultType: ResultType.Ok,
                };
              })
            );

          default:
            return of({
              value: new TableData(0, []),
              isSuccess: true,
              isFailure: false,
              isEmpty: false,
              message: '',
              httpStatusCode: 200,
              resultType: ResultType.Ok,
            });
        }
      })
    );
  }
}
